/**
 * PluginPage — sandboxed iframe host for fleet code-plugins (ADR-047, ADR-048 P1).
 *
 * P1 postMessage bridge:
 *   1. Handshake: on iframe load the plugin sends plugin.ready; the host replies
 *      plugin.init with { fleet_role, theme, plugin:{id,title,data_scopes,api_surface} }.
 *      The console JWT is NEVER forwarded to the iframe.
 *   2. Data RPC: plugin.data.request → host calls GET /api/plugins/:id/data/:sourceId
 *      with its own JWT, replies plugin.data.response.
 *   3. Action RPC: plugin.call → host calls POST /api/plugins/:id/call/:action,
 *      replies plugin.call.response.
 *   4. Files (ADR-206): plugin.files.* — see pluginFilesBridge.ts. Uploads and
 *      downloads happen HERE, in the authenticated page, because the sandbox
 *      has no network and no allow-downloads. ADR-210: so does COPY LINK
 *      (plugin.files.copy_link), because the sandbox has no clipboard permission.
 *
 * Security:
 *   - sandbox="allow-scripts" WITHOUT allow-same-origin → opaque origin.
 *   - Origin check: ev.source === iframeRef.current?.contentWindow (not ev.origin,
 *     which is "null" for opaque-origin iframes).
 *   - targetOrigin='*' on postMessage back to the iframe is correct here — the
 *     sandbox is the security boundary, not origin matching.
 */
import { useEffect, useRef, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { api, ForbiddenError, type PluginManifest } from '../api/client'
import { useAuth } from '../context/AuthContext'
import { handleFilesMessage } from './pluginFilesBridge'

export function PluginPage() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const [plugin, setPlugin] = useState<PluginManifest | null>(null)
  const [error, setError] = useState<string | null>(null)
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  // ADR-206: uploads in flight, so plugin.files.cancel can abort one.
  const uploadsRef = useRef(new Map<string, AbortController>())

  // Fetch the full plugin manifest (with data_scopes + api_surface).
  useEffect(() => {
    if (!id) return
    let cancelled = false
    setPlugin(null)
    setError(null)

    api.getPluginManifest(id).then(p => {
      if (!cancelled) setPlugin(p)
    }).catch(err => {
      if (cancelled) return
      if (err instanceof ForbiddenError) {
        setError('You do not have permission to view this plugin.')
      } else if (err instanceof Error && err.message.includes('404')) {
        setError('Plugin not found.')
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load plugin.')
      }
    })
    return () => { cancelled = true }
  }, [id])

  // postMessage bridge — set up once the manifest is loaded.
  const handleMessage = useCallback((ev: MessageEvent) => {
    // Security: only process messages from the sandboxed iframe's contentWindow.
    // We cannot check ev.origin (it is "null" for opaque-origin sandboxed iframes);
    // ev.source === contentWindow is the correct guard.
    if (!iframeRef.current || ev.source !== iframeRef.current.contentWindow) return
    if (!plugin || !id) return

    const msg = ev.data
    if (!msg || typeof msg !== 'object' || typeof msg.type !== 'string') return

    const iframeWindow = iframeRef.current.contentWindow
    if (!iframeWindow) return

    // ADR-206: a declared file tree (browse, change, upload, download, bytes).
    if (handleFilesMessage(msg, {
      pluginId: id,
      post: (m, transfer) => iframeWindow.postMessage(m, '*', transfer ?? []),
      uploads: uploadsRef.current,
      fileScopes: plugin.data_scopes.filter(d => d.type === 'files').map(d => d.id),
    })) {
      return
    }

    switch (msg.type) {
      case 'plugin.ready': {
        // Handshake: send plugin context — never the JWT.
        iframeWindow.postMessage(
          {
            type: 'plugin.init',
            fleet_role: user.fleet_role,
            theme: 'light',
            plugin: {
              id: plugin.id,
              title: plugin.title,
              data_scopes: plugin.data_scopes,
              api_surface: plugin.api_surface,
            },
          },
          // targetOrigin='*' is correct: the iframe is at an opaque origin and we
          // cannot supply a specific target; the sandbox is the security boundary.
          '*',
        )
        break
      }

      case 'plugin.data.request': {
        const { requestId, sourceId } = msg
        if (typeof requestId !== 'string' || typeof sourceId !== 'string') return

        api.getPluginData(id, sourceId)
          .then(result => {
            iframeWindow.postMessage(
              { type: 'plugin.data.response', requestId, ok: true, data: result.data },
              '*',
            )
          })
          .catch((err: unknown) => {
            iframeWindow.postMessage(
              {
                type: 'plugin.data.response',
                requestId,
                ok: false,
                error: err instanceof Error ? err.message : 'data fetch failed',
              },
              '*',
            )
          })
        break
      }

      case 'plugin.call': {
        const { requestId, action, input } = msg
        if (typeof requestId !== 'string' || typeof action !== 'string') return

        api.callPluginAction(id, action, input)
          .then(result => {
            iframeWindow.postMessage(
              { type: 'plugin.call.response', requestId, ...result },
              '*',
            )
          })
          .catch((err: unknown) => {
            iframeWindow.postMessage(
              {
                type: 'plugin.call.response',
                requestId,
                ok: false,
                status: 'error',
                error: err instanceof Error ? err.message : 'action call failed',
              },
              '*',
            )
          })
        break
      }
    }
  }, [id, plugin, user.fleet_role])

  useEffect(() => {
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [handleMessage])

  // Leaving the page cancels any upload in flight; uploadFile then aborts it
  // on the host, so nothing partial is left behind (ADR-206 D4).
  useEffect(() => {
    const uploads = uploadsRef.current
    return () => {
      uploads.forEach(c => c.abort())
      uploads.clear()
    }
  }, [])

  if (!id) {
    return <p className="text-sm text-red-600">No plugin ID in URL.</p>
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {error}
      </div>
    )
  }

  if (!plugin) {
    return <div className="text-sm text-gray-400 animate-pulse">Loading plugin...</div>
  }

  const src = `/plugins/${encodeURIComponent(plugin.id)}/assets/${plugin.entry}`

  return (
    <div className="flex flex-col h-full space-y-0 md:space-y-3">
      {/* Hidden on a phone. The shell's own header already names the page — this is the same
          word a second time, in `text-2xl` with an outsized emoji beside it, costing ~56px of
          a ~700px viewport before the plugin draws anything. The version is not worth a line
          of a phone screen; it is still here at every other width. */}
      <div className="hidden md:flex items-baseline gap-2">
        <h1 className="text-2xl font-semibold text-gray-900">
          {plugin.icon ? `${plugin.icon} ` : ''}{plugin.title}
        </h1>
        <span className="text-xs text-gray-400">v{plugin.version}</span>
      </div>

      {/*
        P1 trust boundary: sandbox="allow-scripts" WITHOUT allow-same-origin.
        The plugin runs with an opaque origin. All data flows through the
        postMessage bridge above — the console JWT is never forwarded.
      */}
      <iframe
        ref={iframeRef}
        src={src}
        sandbox="allow-scripts"
        referrerPolicy="no-referrer"
        title={plugin.title}
        className="flex-1 w-full border-0 md:border border-gray-200 rounded-none md:rounded-lg bg-white min-h-[320px] md:min-h-[500px]"
      />
    </div>
  )
}
